import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import { RequestService } from "../services/requestService";
import { ContractListener } from "../services/contractListener";
import init, {
  decrypt_ciphertext,
  decode_transfer_proof,
} from "../crypto/confidentialCrypto.js";
import { CHAINS, getChainById, isTokenEnabledForNetwork } from "../config/chains";
import {
  extractIpfsCidFromProofText,
  ipfsGatewayUrlForCid,
} from "../utils/ipfs";
import { getCleanErrorMessage } from "../utils/errorParser.js";
import "./History.css";

const resolveChainConfig = (chainKey) => {
  if (!chainKey) {
    return null;
  }

  // Config key first (arbitrum vs arbitrum_mainnet), then EVM id via shared helper
  return getChainById(chainKey);
};

const formatStatusLabel = (status) => {
  if (!status) return "Pending";

  const normalized = status.toLowerCase();
  switch (normalized) {
    case "success":
      return "Completed";
    case "failed":
      return "Failed";
    case "processing":
      return "Processing";
    case "pending":
      return "Pending";
    default:
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
};

const formatHash = (hash) => {
  if (!hash || hash.length <= 12) {
    return hash || "";
  }
  return `${hash.slice(0, 10)}...${hash.slice(-6)}`;
};

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const History = ({ userAccount, contract, provider, keypair, chainId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decryptingTxId, setDecryptingTxId] = useState(null);
  const [decryptedAmounts, setDecryptedAmounts] = useState({});
  const backfilledRef = useRef(new Set()); // Track which account+chain combos have been backfilled

  const CONTRACT_ABI = [
    "event CreateAccountRequested(address indexed ownerAddr, uint256 indexed txId, bytes elgamalPubkey)",
    "event CreateAccountProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, uint8 code, bytes elgamalPubkey)",
    "event DepositRequested(address indexed ownerAddr, uint256 indexed txId, address indexed token, uint256 plainAmount, bytes availableC1, bytes availableC2)",
    "event DepositProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, uint8 code, address token, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter)",
    "event TransferRequested(address indexed sender, address indexed recipient, uint256 indexed txId, address token, bytes proof, bytes senderPubkey, bytes recipientPubkey, bytes senderCurrC1, bytes senderCurrC2, bool offchainZKP)",
    "event TransferProcessed(address indexed sender, address indexed recipient, uint256 indexed txId, bool ok, uint8 code, address token, bytes senderNewAvailC1, bytes senderNewAvailC2, bytes recipientNewPendingC1, bytes recipientNewPendingC2, uint64 senderPendingCreditCounter, uint64 recipientPendingCreditCounter)",
    "event ApplyPendingRequested(address indexed ownerAddr, uint256 indexed txId)",
    "event ApplyPendingProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, uint8 code, address[] tokens, bytes[] newAvailC1, bytes[] newAvailC2, uint64 pendingCreditCounter)",
    "event WithdrawRequested(address indexed ownerAddr, uint256 indexed txId, address token, uint256 plainAmount, bytes proof, bytes userPubkey, bytes userCurrC1, bytes userCurrC2, bool offchainZKP)",
    "event WithdrawProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, uint8 code, address token, uint256 plainAmount, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter)",
  ];

  /**
   * Resolve a token only when it is a known, enabled app token for that chain.
   * Never fall back to USDC — inactive / unknown assets must not be mislabeled.
   * @returns {{ symbol: string, decimals: number } | null}
   */
  const getTokenInfoFromChain = (tokenIdentifier, reqChainId) => {
    if (!tokenIdentifier) return null;

    const txChainConfig = resolveChainConfig(reqChainId || chainId);
    if (!txChainConfig?.tokens) return null;

    const normalizedIdentifier = String(tokenIdentifier).toLowerCase().trim();
    let matched = null;

    if (
      normalizedIdentifier.startsWith("0x") &&
      normalizedIdentifier.length === 42
    ) {
      for (const tokenKey in txChainConfig.tokens) {
        const token = txChainConfig.tokens[tokenKey];
        const addr = token.address?.toLowerCase?.();
        if (
          addr &&
          addr !== ZERO_ADDRESS &&
          addr === normalizedIdentifier
        ) {
          matched = token;
          break;
        }
      }
    } else {
      for (const tokenKey in txChainConfig.tokens) {
        const token = txChainConfig.tokens[tokenKey];
        if (
          token.symbol &&
          token.symbol.toLowerCase() === normalizedIdentifier
        ) {
          matched = token;
          break;
        }
      }
    }

    if (!matched) return null;
    if (
      !isTokenEnabledForNetwork(
        txChainConfig.id,
        matched.symbol,
        txChainConfig.network,
      )
    ) {
      return null;
    }

    return { symbol: matched.symbol, decimals: matched.decimals };
  };

  /** Hide deposits/withdraws/transfers for tokens the UI does not support. */
  const shouldIncludeHistoryRequest = (req) => {
    const kind = req.operationKind;
    const tokenRef = req.details?.tokenAddress || req.details?.token;
    // Deposits and withdrawals always carry a token — drop unsupported ones
    // so they are never shown as USDC.
    if (kind === 1 || kind === 4) {
      return !!getTokenInfoFromChain(tokenRef, req.chainId || chainId);
    }
    // Transfers: when we have a concrete token address, require it to be supported.
    if (kind === 2 && tokenRef) {
      const asAddr = String(tokenRef).toLowerCase();
      if (asAddr.startsWith("0x") && asAddr.length === 42) {
        return !!getTokenInfoFromChain(tokenRef, req.chainId || chainId);
      }
    }
    return true;
  };

  const chainConfig = resolveChainConfig(chainId);
  const chainDisplayName = chainConfig?.name;

  const buildExplorerLink = (transaction) => {
    if (!transaction?.transactionHash) {
      return null;
    }

    const txChainConfig = resolveChainConfig(transaction.chainId || chainId);
    const baseUrl = txChainConfig?.blockExplorerUrl;

    if (!baseUrl) {
      return null;
    }

    const separator = baseUrl.endsWith("/") ? "" : "/";
    return `${baseUrl}${separator}tx/${transaction.transactionHash}`;
  };

  useEffect(() => {
    let unsubscribe = () => {};

    const subscribe = async () => {
      if (!userAccount || !contract || !provider) {
        setTransactions([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      // Backfill received transactions on first load (only once per account+chain)
      const backfillKey = `${userAccount}-${chainId}`;
      if (!backfilledRef.current.has(backfillKey)) {
        try {
          const listener = new ContractListener(contract, contract.address);
          const created =
            await listener.backfillReceivedTransactions(userAccount);
          backfilledRef.current.add(backfillKey);
        } catch (error) {
          console.error("Error backfilling received transactions:", error);
        }
      }

      // Normalize chainId to numeric format for filtering (chainId prop might be string like 'arbitrum')
      const numericChainId = (() => {
        if (!chainId) return null;
        // If it's already a chain key (string), get numeric chainId from config
        if (typeof chainId === "string" && CHAINS[chainId]) {
          const chainConfig = CHAINS[chainId];
          return (
            chainConfig.chainIdDecimal ||
            (typeof chainConfig.chainId === "string" &&
            chainConfig.chainId.startsWith("0x")
              ? parseInt(chainConfig.chainId, 16)
              : Number(chainConfig.chainId))
          );
        }
        // If it's a hex string, convert to number
        if (typeof chainId === "string" && chainId.startsWith("0x")) {
          return parseInt(chainId, 16);
        }
        // Otherwise, try to convert to number
        return Number(chainId);
      })();

      // Subscribe to Firestore requests filtered by current chainId
      unsubscribe = RequestService.subscribeToUserRequests(
        userAccount,
        async (requests) => {
          try {
            // Map Firestore requests to history items (without expensive chain queries).
            // Drop deposits/withdraws/transfers for tokens the app does not support
            // so they never appear mislabeled as USDC.
            const mappedRequests = requests
              .filter(shouldIncludeHistoryRequest)
              .map((req) => {
              const txType = mapOperationKindToType(
                req.operationKind,
                req.type,
              );
              // Extract actual requestId from document ID
              // Format can be: "chainId:userAddress:requestId", "chainId:requestId", or just "requestId"
              const requestId = req.requestId || req.id;
              let actualRequestId = requestId;
              if (requestId.includes(":")) {
                const parts = requestId.split(":");
                // If format is chainId:userAddress:requestId (3 parts) or chainId:requestId (2 parts),
                // take the last part which is the actual requestId
                actualRequestId = parts[parts.length - 1];
              }

              return {
                id: actualRequestId, // Use actual requestId for display
                date: req.createdAt?.toDate
                  ? formatDate(req.createdAt.toDate())
                  : formatDate(new Date()),
                type: txType,
                status: req.status || "pending",
                amount: deriveAmountForRequest(req),
                // For received txs, show sender; for send txs, show recipient
                address:
                  txType === "received"
                    ? req.details?.sender
                    : req.details?.recipient || req.userAddress,
                timestamp: req.createdAt?.toDate
                  ? req.createdAt.toDate()
                  : new Date(),
                operation: mapOperationKindToOperation(req.operationKind),
                transactionHash: req.transactionHash,
                blockNumber: req.blockNumber,
                chainId: req.chainId, // Store chainId for decryption
              };
            });


            // Sort by timestamp (newest first)
            mappedRequests.sort(
              (a, b) =>
                (b.timestamp?.getTime?.() || 0) -
                (a.timestamp?.getTime?.() || 0),
            );

            setTransactions(mappedRequests);
          } catch (e) {
            console.error("Failed mapping requests to history:", e);
          } finally {
            setLoading(false);
          }
        },
        numericChainId,
      ); // Pass numeric chainId to filter by current chain
    };

    subscribe();
    return () => unsubscribe();
  }, [userAccount, contract, provider, chainId]);

  const mapRequestsToHistoryItems = async (
    requests,
    contractInstance,
    provider,
    userAddress,
  ) => {
    // For each request, derive UI item and enrich with onchain processed status
    const items = await Promise.all(
      requests.filter(shouldIncludeHistoryRequest).map(async (req) => {
        const base = {
          id: req.requestId || req.id,
          date: req.createdAt?.toDate
            ? formatDate(req.createdAt.toDate())
            : formatDate(new Date()),
          type: mapOperationKindToType(req.operationKind, req.type),
          status: req.status || "pending",
          amount: deriveAmountForRequest(req),
          address: req.details?.recipient || req.userAddress,
          timestamp: req.createdAt?.toDate
            ? req.createdAt.toDate()
            : new Date(),
          operation: mapOperationKindToOperation(req.operationKind),
          transactionHash: req.transactionHash,
          blockNumber: undefined,
        };

        // Enrich from chain using requestId (txId)
        try {
          const enriched = await enrichStatusFromChain(
            base,
            req,
            contractInstance,
            provider,
            userAddress,
          );
          return enriched || base;
        } catch (_) {
          return base;
        }
      }),
    );

    // Sort newest first by timestamp if present
    items.sort(
      (a, b) =>
        (b.timestamp?.getTime?.() || 0) - (a.timestamp?.getTime?.() || 0),
    );
    return items;
  };

  const mapOperationKindToOperation = (kind) => {
    switch (kind) {
      case 1:
        return "deposit";
      case 2:
        return "transfer";
      case 3:
        return "apply";
      case 4:
        return "withdraw";
      default:
        return "unknown";
    }
  };

  const mapOperationKindToType = (kind, legacyType) => {
    if (typeof kind !== "number") return (legacyType || "").toLowerCase();
    if (kind === 1) return "deposit";
    if (kind === 2) {
      // For transfers, respect the 'received' type if explicitly set
      if (legacyType === "received") return "received";
      return "send";
    }
    if (kind === 3) return "apply";
    if (kind === 4) return "withdraw";
    return "unknown";
  };

  const deriveAmountForRequest = (req) => {
    // Deposits, withdrawals, and secure claim are public; transfers are confidential
    if (
      req.operationKind === 1 ||
      req.operationKind === 4 ||
      req.operationKind === 3
    ) {
      // Get token address and chainId from request
      const tokenAddress = req.details?.tokenAddress || req.details?.token;
      const reqChainId = req.chainId || chainId;

      // Get token info from chain configuration (null if unsupported / unknown)
      const tokenInfo = getTokenInfoFromChain(tokenAddress, reqChainId);
      if (!tokenInfo) {
        return null;
      }

      let amount, tokenSymbol;

      if (req.details?.amount && req.details?.token) {
        amount = req.details.amount;
        // Use token symbol from chain configuration instead of database value
        tokenSymbol = tokenInfo.symbol;
      } else if (req.details?.amount && req.type) {
        amount = req.details.amount;
        // Use token symbol from chain configuration
        tokenSymbol = tokenInfo.symbol;
      } else if (req.details?.pendingAmount && req.details?.token) {
        amount = req.details.pendingAmount;
        // Use token symbol from chain configuration
        tokenSymbol = tokenInfo.symbol;
      }

      if (amount && tokenSymbol) {
        // Amounts stored in database are in contract format (amount * 100)
        // To display, divide by 100 to get user-facing amount
        const numericAmount =
          typeof amount === "string" ? parseFloat(amount) : parseFloat(amount);

        if (!isNaN(numericAmount)) {
          // Check if amount is in raw format (amountInWei flag or integer >= 1)
          const isRawFormat =
            req.details?.amountInWei === true ||
            (typeof amount === "string" && amount.includes("e")) || // Scientific notation indicates large number
            (Number.isInteger(numericAmount) && numericAmount >= 1);

          if (isRawFormat) {
            // Amount is in contract format (amount * 100), convert to user-facing format
            const convertedAmount = numericAmount / 100;
            let formattedAmount = convertedAmount.toFixed(2);

            // Remove trailing zeros
            formattedAmount = formattedAmount.replace(/\.?0+$/, "");

            // Ensure we show at least something if value is > 0
            if (
              convertedAmount > 0 &&
              (formattedAmount === "" || formattedAmount === "0")
            ) {
              formattedAmount = convertedAmount.toFixed(2);
            }

            return `${formattedAmount} ${tokenSymbol}`;
          } else {
            // Legacy: Amount is in user-facing format (old records only)
            let formattedAmount = numericAmount.toFixed(2);

            // Remove trailing zeros
            formattedAmount = formattedAmount.replace(/\.?0+$/, "");

            // Ensure we show at least something if value is > 0
            if (
              numericAmount > 0 &&
              (formattedAmount === "" || formattedAmount === "0")
            ) {
              formattedAmount = numericAmount.toFixed(2);
            }

            return `${formattedAmount} ${tokenSymbol}`;
          }
        }
        return `${amount} ${tokenSymbol}`;
      }
    }
    return null; // confidential or unknown
  };

  const enrichStatusFromChain = async (
    base,
    req,
    contractInstance,
    provider,
    userAddress,
  ) => {
    const txId = req.requestId || req.id;
    if (!txId) return base;

    const bnTxId = ethers.BigNumber.from(txId);
    let event;
    if (base.operation === "deposit") {
      const logs = await contractInstance.queryFilter(
        contractInstance.filters.DepositProcessed(userAddress, bnTxId),
      );
      event = logs[0];
    } else if (base.operation === "withdraw") {
      const logs = await contractInstance.queryFilter(
        contractInstance.filters.WithdrawProcessed(userAddress, bnTxId),
      );
      event = logs[0];
    } else if (base.operation === "transfer") {
      // Check both sender and recipient cases
      const logsSender = await contractInstance.queryFilter(
        contractInstance.filters.TransferProcessed(userAddress, null, bnTxId),
      );
      const logsRecipient = await contractInstance.queryFilter(
        contractInstance.filters.TransferProcessed(null, userAddress, bnTxId),
      );
      event = logsSender[0] || logsRecipient[0];

      // Update address based on whether user was sender or recipient
      if (logsSender[0]) {
        // User was sender, show recipient address
        base.address = logsSender[0].args.recipient;
      } else if (logsRecipient[0]) {
        // User was recipient, show sender address
        base.address = logsRecipient[0].args.sender;
      }
    } else if (base.operation === "apply") {
      const logs = await contractInstance.queryFilter(
        contractInstance.filters.ApplyPendingProcessed(userAddress, bnTxId),
      );
      event = logs[0];
    }

    if (!event) return base;

    const block = await provider.getBlock(event.blockNumber);
    const ts = new Date(block.timestamp * 1000);
    const okArg = event.args?.ok;
    return {
      ...base,
      status: okArg === false ? "failed" : "success",
      timestamp: ts,
      date: formatDate(ts),
      blockNumber: event.blockNumber,
    };
  };

  const fetchAllTransactionEvents = async (
    userAddress,
    contractInstance,
    provider,
  ) => {
    try {

      // Get current block number to limit search range
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - 10000); // Search last ~10k blocks


      // Create contract interface for event parsing
      const contractInterface = new ethers.utils.Interface(CONTRACT_ABI);

      // Fetch all relevant events in parallel
      const [
        depositRequested,
        depositProcessed,
        transferRequested,
        transferProcessed,
        withdrawRequested,
        withdrawProcessed,
        applyPendingRequested,
        applyPendingProcessed,
      ] = await Promise.all([
        contractInstance.queryFilter(
          contractInstance.filters.DepositRequested(userAddress),
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.DepositProcessed(userAddress),
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.TransferRequested(userAddress),
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.TransferProcessed(null, userAddress), // As recipient
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.WithdrawRequested(userAddress),
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.WithdrawProcessed(userAddress),
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.ApplyPendingRequested(userAddress),
          fromBlock,
          currentBlock,
        ),
        contractInstance.queryFilter(
          contractInstance.filters.ApplyPendingProcessed(userAddress),
          fromBlock,
          currentBlock,
        ),
      ]);


      // Process and combine all events
      const allTransactions = [];

      // Process deposits
      for (const event of depositProcessed) {
        const args = event.args;
        const tokenInfo = TOKEN_ADDRESSES[args.token] || {
          symbol: "UNKNOWN",
          decimals: 18,
        };
        // Contract now stores amounts as amount * 100; plainAmount from events is in that format
        const amount = (Number(args.plainAmount) / 100)
          .toFixed(2)
          .replace(/\.?0+$/, "");

        // Get actual block timestamp
        const block = await provider.getBlock(event.blockNumber);
        const timestamp = new Date(block.timestamp * 1000);

        allTransactions.push({
          id: `${event.transactionHash}-${event.logIndex}`,
          date: formatDate(timestamp),
          type: "deposit",
          status: args.ok ? "success" : "failed",
          amount: `${amount} ${tokenInfo.symbol}`,
          address: userAddress,
          timestamp: timestamp,
          operation: "deposit",
          transactionHash: event.transactionHash,
          blockNumber: event.blockNumber,
        });
      }

      // Process transfers (confidential - no amounts shown)
      for (const event of transferRequested) {
        const args = event.args;
        const block = await provider.getBlock(event.blockNumber);
        const timestamp = new Date(block.timestamp * 1000);

        allTransactions.push({
          id: `${event.transactionHash}-${event.logIndex}`,
          date: formatDate(timestamp),
          type: "send",
          status: "pending", // Will be updated when processed
          amount: null, // Confidential transfer
          address: args.recipient,
          timestamp: timestamp,
          operation: "transfer",
          transactionHash: event.transactionHash,
          blockNumber: event.blockNumber,
        });
      }

      // Update transfer status from processed events
      for (const event of transferProcessed) {
        const args = event.args;
        const existingTx = allTransactions.find(
          (tx) =>
            tx.transactionHash === event.transactionHash &&
            tx.operation === "transfer",
        );

        if (existingTx) {
          existingTx.status = args.ok ? "success" : "failed";
        } else {
          // Transfer received (user was recipient)
          const block = await provider.getBlock(event.blockNumber);
          const timestamp = new Date(block.timestamp * 1000);

          allTransactions.push({
            id: `${event.transactionHash}-${event.logIndex}`,
            date: formatDate(timestamp),
            type: "received",
            status: args.ok ? "success" : "failed",
            amount: null, // Confidential transfer
            address: args.sender,
            timestamp: timestamp,
            operation: "transfer",
            transactionHash: event.transactionHash,
            blockNumber: event.blockNumber,
          });
        }
      }

      // Process withdrawals
      for (const event of withdrawProcessed) {
        const args = event.args;
        const tokenInfo = TOKEN_ADDRESSES[args.token] || {
          symbol: "UNKNOWN",
          decimals: 18,
        };
        // Contract now stores amounts as amount * 100; plainAmount from events is in that format
        const amount = (Number(args.plainAmount) / 100)
          .toFixed(2)
          .replace(/\.?0+$/, "");

        const block = await provider.getBlock(event.blockNumber);
        const timestamp = new Date(block.timestamp * 1000);

        allTransactions.push({
          id: `${event.transactionHash}-${event.logIndex}`,
          date: formatDate(timestamp),
          type: "withdraw",
          status: args.ok ? "success" : "failed",
          amount: `${amount} ${tokenInfo.symbol}`,
          address: userAddress,
          timestamp: timestamp,
          operation: "withdraw",
          transactionHash: event.transactionHash,
          blockNumber: event.blockNumber,
        });
      }

      // Sort by block number (most recent first)
      allTransactions.sort((a, b) => b.blockNumber - a.blockNumber);

      return allTransactions;
    } catch (error) {
      console.error("Error fetching transaction events:", error);
      throw error;
    }
  };

  const formatDate = (date) => {
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${dateStr} at ${timeStr}`;
  };

  const getTransactionIcon = (type, status) => {
    if (type === "send") {
      return (
        <div
          className={`transaction-icon ${status === "failed" ? "failed" : "send"}`}
        >
          <i className="fas fa-arrow-up"></i>
        </div>
      );
    } else if (type === "received") {
      return (
        <div className="transaction-icon received">
          <i className="fas fa-arrow-down"></i>
        </div>
      );
    } else if (type === "deposit") {
      return (
        <div className="transaction-icon deposit">
          <i className="fas fa-arrow-down"></i>
        </div>
      );
    } else if (type === "withdraw") {
      return (
        <div className="transaction-icon withdraw">
          <i className="fas fa-arrow-up"></i>
        </div>
      );
    } else if (type === "apply") {
      return (
        <div className="transaction-icon apply">
          <i className="fas fa-check-double"></i>
        </div>
      );
    }
    return null;
  };

  const getStatusIcon = (status) => {
    if (status === "success") {
      return <i className="fas fa-check-circle success-icon"></i>;
    } else if (status === "failed") {
      return <i className="fas fa-times-circle failed-icon"></i>;
    }
    return null;
  };

  const getTransactionTypeText = (type, status) => {
    if (type === "send") {
      return status === "failed" ? "Transaction Failed" : "Sent";
    } else if (type === "received") {
      return "Received";
    } else if (type === "deposit") {
      return "Deposited";
    } else if (type === "withdraw") {
      return "Withdrawn";
    } else if (type === "apply") {
      return "Claimed";
    }
    return "Unknown";
  };

  const getTransactionTypeClass = (type, status) => {
    if (status === "failed") return "failed";
    if (type === "send") return "send";
    if (type === "received") return "received";
    if (type === "deposit") return "deposit";
    if (type === "withdraw") return "withdraw";
    return "";
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  const getAddressLabel = (transaction) => {
    if (transaction.type === "send") {
      return "To";
    } else if (transaction.type === "received") {
      return "From";
    } else if (
      transaction.type === "deposit" ||
      transaction.type === "withdraw"
    ) {
      return "Address";
    } else if (transaction.type === "apply") {
      return "Account";
    }
    return "Address";
  };

  const getProviderForChain = (txChainId) => {
    const targetChain = resolveChainConfig(txChainId);

    if (!targetChain || !targetChain.rpcUrl) {
      throw new Error(`Chain ${txChainId} not found in configuration`);
    }

    // Reuse existing provider if chain matches current selection
    if (chainConfig && targetChain.id === chainConfig.id && provider) {
      return provider;
    }

    return new ethers.providers.JsonRpcProvider(targetChain.rpcUrl);
  };

  const fetchRawTransactionInput = async (txProvider, txHash) => {
    try {
      const raw = await txProvider.send("eth_getTransactionByHash", [txHash]);
      if (raw && typeof raw.input === "string") {
        return raw.input;
      }
      // Some providers expose calldata as `data` instead of `input`.
      if (raw && typeof raw.data === "string" && raw.data !== "0x") {
        return raw.data;
      }
    } catch (err) {
      console.warn(
        "Raw eth_getTransactionByHash failed, falling back:",
        err?.message || err,
      );
    }

    const tx = await txProvider.getTransaction(txHash);
    if (!tx) {
      return null;
    }
    return tx.data;
  };

  /** Extract on-chain transfer txId from history ids like `42` or `received-42-0xabc...`. */
  const extractTransferTxId = (requestId) => {
    if (requestId == null || requestId === "") return null;
    const raw = String(requestId);
    if (raw.startsWith("received-")) {
      const parts = raw.split("-");
      return parts.length >= 2 ? parts[1] : null;
    }
    // Doc ids may be `chainId:user:txId` — keep the last segment.
    if (raw.includes(":")) {
      const parts = raw.split(":");
      return parts[parts.length - 1];
    }
    return raw;
  };

  const getContractForChain = (txChainId) => {
    const targetChain = resolveChainConfig(txChainId);
    if (!targetChain?.contractAddress) {
      throw new Error(`Contract not configured for chain ${txChainId}`);
    }
    const txProvider = getProviderForChain(txChainId);
    // Reuse the live contract when it already points at this chain.
    if (
      contract &&
      provider &&
      chainConfig &&
      targetChain.id === chainConfig.id &&
      contract.address?.toLowerCase() ===
        targetChain.contractAddress.toLowerCase()
    ) {
      return contract;
    }
    return new ethers.Contract(
      targetChain.contractAddress,
      CONTRACT_ABI,
      txProvider,
    );
  };

  /**
   * Prefer TransferRequested event logs (indexed by txId) over eth_getTransactionByHash.
   * History often stores the TransferProcessed hash, which may differ from the
   * user request tx or be missing on public RPCs — the event still has the proof.
   */
  const fetchTransferProofFromEvents = async (transaction, txChainId) => {
    const txId = extractTransferTxId(transaction.id);
    if (txId == null || !/^\d+$/.test(String(txId))) {
      return null;
    }

    const chainContract = getContractForChain(txChainId);
    const bnTxId = ethers.BigNumber.from(txId);
    const txProvider = chainContract.provider || getProviderForChain(txChainId);

    let fromBlock = 0;
    try {
      if (transaction.blockNumber) {
        fromBlock = Math.max(0, Number(transaction.blockNumber) - 500);
      } else {
        const latest = await txProvider.getBlockNumber();
        fromBlock = Math.max(0, latest - 100000);
      }
    } catch (_) {
      fromBlock = 0;
    }

    // Try current ABI first, then legacy (no offchainZKP) for older deployments.
    const filtersToTry = [];
    if (typeof chainContract.filters?.TransferRequested === "function") {
      filtersToTry.push(
        chainContract.filters.TransferRequested(null, null, bnTxId),
      );
    }

    let logs = [];
    for (const filter of filtersToTry) {
      try {
        logs = await chainContract.queryFilter(filter, fromBlock, "latest");
        if (logs.length) break;
      } catch (err) {
        console.warn(
          "TransferRequested query failed, retrying without block range:",
          err?.message || err,
        );
        try {
          logs = await chainContract.queryFilter(filter);
          if (logs.length) break;
        } catch (_) {
          // continue
        }
      }
    }

    // Legacy event without offchainZKP bool — parse manually if needed.
    if (!logs.length) {
      try {
        const legacyIface = new ethers.utils.Interface([
          "event TransferRequested(address indexed sender, address indexed recipient, uint256 indexed txId, address token, bytes proof, bytes senderPubkey, bytes recipientPubkey, bytes senderCurrC1, bytes senderCurrC2)",
        ]);
        const topicTxId = ethers.utils.hexZeroPad(
          ethers.utils.hexlify(bnTxId),
          32,
        );
        const topic0 = legacyIface.getEventTopic("TransferRequested");
        const rawLogs = await txProvider.getLogs({
          address: chainContract.address,
          fromBlock,
          toBlock: "latest",
          topics: [topic0, null, null, topicTxId],
        });
        logs = rawLogs
          .map((log) => {
            try {
              return {
                args: legacyIface.parseLog(log).args,
                transactionHash: log.transactionHash,
              };
            } catch {
              return null;
            }
          })
          .filter(Boolean);
      } catch (err) {
        console.warn(
          "Legacy TransferRequested lookup failed:",
          err?.message || err,
        );
      }
    }

    if (!logs.length) return null;

    const evt = logs[0];
    const args = evt.args || evt;
    return {
      tokenAddress: args.token,
      proofArg: args.proof,
      requestTxHash: evt.transactionHash || null,
    };
  };

  const fetchTransferProofFromCalldata = async (txProvider, txHash) => {
    if (!txHash) return null;

    const txInput = await fetchRawTransactionInput(txProvider, txHash);
    if (!txInput || txInput === "0x") {
      return null;
    }

    // Prefer the SDK 2.0.11+ 4-arg signature; fall back to the legacy 3-arg form.
    let decodedInput;
    try {
      const iface = new ethers.utils.Interface([
        "function transferConfidential(address recipient, address token, bytes proof, bool offchainZKP)",
      ]);
      decodedInput = iface.parseTransaction({ data: txInput });
    } catch (_) {
      const legacyIface = new ethers.utils.Interface([
        "function transferConfidential(address recipient, address token, bytes proof)",
      ]);
      decodedInput = legacyIface.parseTransaction({ data: txInput });
    }

    return {
      tokenAddress: decodedInput.args.token,
      proofArg: decodedInput.args.proof,
    };
  };

  const resolveTransferProofBytes = async (proofArg) => {
    if (!proofArg) {
      throw new Error("Missing transfer proof bytes");
    }

    const rawHex =
      typeof proofArg === "string" ? proofArg : ethers.utils.hexlify(proofArg);

    // Off-chain proofs are stored as UTF-8 CID bytes (bare CID in SDK 2.0.11+,
    // or legacy `ipfs://<cid>`). Resolve to original ABI-encoded proof bytes.
    try {
      const pointer = ethers.utils.toUtf8String(rawHex).trim();
      const cid = extractIpfsCidFromProofText(pointer);
      if (cid) {
        const response = await fetch(ipfsGatewayUrlForCid(cid));
        if (!response.ok) {
          throw new Error(
            `Failed to fetch proof from IPFS (${response.status})`,
          );
        }

        const proofBuffer = await response.arrayBuffer();
        return ethers.utils.hexlify(new Uint8Array(proofBuffer));
      }
    } catch (_) {
      // Not a UTF-8 pointer payload; treat as direct ABI proof bytes.
    }

    return rawHex;
  };

  const decryptTransferAmount = async (transaction) => {
    if (!keypair) {
      console.error("Missing required data for decryption");
      return;
    }

    setDecryptingTxId(transaction.id);

    try {
      // Initialize WASM module
      await init();

      // Get the correct provider for this transaction's chain
      const txChainId = transaction.chainId || chainId;
      const txProvider = getProviderForChain(txChainId);

      // 1) Prefer TransferRequested logs (proof is in the event; works even when
      //    history stored a TransferProcessed hash that RPCs can't resolve).
      let proofSource = await fetchTransferProofFromEvents(
        transaction,
        txChainId,
      );

      // 2) Fall back to decoding transferConfidential calldata by tx hash.
      if (!proofSource) {
        proofSource = await fetchTransferProofFromCalldata(
          txProvider,
          transaction.transactionHash,
        );
      }

      if (!proofSource?.proofArg) {
        throw new Error(
          "Could not load transfer proof (transaction or event not found)",
        );
      }

      const tokenAddress = proofSource.tokenAddress;
      const proofData = await resolveTransferProofBytes(proofSource.proofArg);

      // Get token info from chain configuration
      const tokenInfo = getTokenInfoFromChain(tokenAddress, txChainId);
      const tokenSymbol = tokenInfo?.symbol || "TOKEN";

      // The proof is ABI-encoded as (string, string, string) - equality_proof, ciphertext_validity_proof, range_proof
      const decoded = ethers.utils.defaultAbiCoder.decode(
        ["string", "string", "string"],
        proofData,
      );
      const validityProofBase64 = decoded[1]; // Use the validity proof (second parameter)

      // Determine if user is sender or receiver
      // transaction.type is 'send' if user is sender, 'received' if user is receiver
      const isSender = transaction.type === "send";
      const handleIndex = isSender ? 0 : 1; // 0 for sender, 1 for recipient


      // Decode the validity proof to extract the transfer amount ciphertext
      const proofDecodeResult = JSON.parse(
        decode_transfer_proof(validityProofBase64, handleIndex),
      );
      if (!proofDecodeResult.success) {
        throw new Error("Failed to decode validity proof");
      }

      const transferAmountCiphertextBase64 =
        proofDecodeResult.transfer_amount_ciphertext;

      // Decrypt the transfer amount directly
      const transferAmountResult = JSON.parse(
        decrypt_ciphertext(transferAmountCiphertextBase64, keypair.private_key),
      );
      const transferAmountRaw = transferAmountResult.decrypted_amount;
      // Contract stores balances as amount * 100, so divide by 100 for display
      const transferAmount = transferAmountRaw / 100;


      // Format the amount with 2 decimal places (contract precision)
      let formattedAmount = transferAmount.toFixed(2);

      // Remove trailing zeros
      formattedAmount = formattedAmount.replace(/\.?0+$/, "");

      // Ensure we show at least something if value is > 0
      if (
        transferAmount > 0 &&
        (formattedAmount === "" || formattedAmount === "0")
      ) {
        formattedAmount = transferAmount.toFixed(2);
      }

      setDecryptedAmounts((prev) => ({
        ...prev,
        [transaction.id]: `${formattedAmount} ${tokenSymbol}`,
      }));
    } catch (error) {
      console.error("Error decrypting transfer amount:", error);
      setDecryptedAmounts((prev) => ({
        ...prev,
        [transaction.id]: `Error: ${getCleanErrorMessage(error) || "Decryption failed"}`,
      }));
    } finally {
      setDecryptingTxId(null);
    }
  };

  const hexToBytes = (hex) => {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  };

  if (!userAccount) {
    return (
      <div className="history-container">
        <div className="history-state no-account-message">
          <h3>Connect Your Wallet</h3>
          <p>Please connect your wallet to view transaction history.</p>
        </div>
      </div>
    );
  }

  const formattedAccount = formatAddress(userAccount);

  return (
    <div className="history-container">
      {(formattedAccount || chainDisplayName) && (
        <div className="history-meta">
          {formattedAccount && (
            <span className="history-chip">{formattedAccount}</span>
          )}
          {chainDisplayName && (
            <span className="history-chip">{chainDisplayName}</span>
          )}
        </div>
      )}
      {loading ? (
        <div className="history-state loading-container">
          <div className="loading-spinner">
            <i className="fas fa-circle-notch fa-spin"></i>
          </div>
          <p>Loading transaction history...</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="history-state empty-state">
          <div className="empty-icon">
            <i className="fas fa-history"></i>
          </div>
          <h3>No Transactions Yet</h3>
          <p>
            Your transaction history will appear here once you start making
            transfers.
          </p>
        </div>
      ) : (
        <div className="transactions-list">
          {transactions.map((transaction) => {
            const amountClass = transaction.amount
              ? getTransactionTypeClass(transaction.type, transaction.status)
              : "confidential";
            const explorerLink = buildExplorerLink(transaction);
            const statusClass = (transaction.status || "pending").toLowerCase();
            const showStatus = false; // Hide status indicators to match design
            const statusLabel = showStatus
              ? formatStatusLabel(transaction.status)
              : null;

            return (
              <div key={transaction.id} className="transaction-item">
                <div className="transaction-date">{transaction.date}</div>
                <div className="transaction-details">
                  <div className="transaction-main">
                    <div className="transaction-type-section">
                      {getTransactionIcon(transaction.type, transaction.status)}
                      <span
                        className={`transaction-type ${getTransactionTypeClass(transaction.type, transaction.status)}`}
                      >
                        {getTransactionTypeText(
                          transaction.type,
                          transaction.status,
                        )}
                      </span>
                    </div>
                    <div className="transaction-meta">
                      {showStatus && (
                        <span className={`transaction-status ${statusClass}`}>
                          {getStatusIcon(transaction.status)}
                          {statusLabel}
                        </span>
                      )}
                      <div className="transaction-amount-wrapper">
                        {decryptedAmounts[transaction.id] ? (
                          <div
                            className={`transaction-amount ${getTransactionTypeClass(transaction.type, transaction.status)}`}
                          >
                            {decryptedAmounts[transaction.id]}
                          </div>
                        ) : transaction.amount ? (
                          <div className={`transaction-amount ${amountClass}`}>
                            {transaction.amount}
                          </div>
                        ) : (
                          <div className="transaction-amount-confidential">
                            <div
                              className={`transaction-amount ${amountClass}`}
                            >
                              Confidential
                            </div>
                            {(transaction.type === "send" ||
                              transaction.type === "received") &&
                              (transaction.transactionHash ||
                                extractTransferTxId(transaction.id)) &&
                              keypair && (
                                <button
                                  className="decrypt-button"
                                  onClick={() =>
                                    decryptTransferAmount(transaction)
                                  }
                                  disabled={decryptingTxId === transaction.id}
                                >
                                  {decryptingTxId === transaction.id
                                    ? "Decrypting..."
                                    : "Decrypt Amount"}
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {transaction.type !== "deposit" &&
                    transaction.type !== "withdraw" &&
                    transaction.type !== "apply" && (
                      <div className="transaction-address">
                        {getAddressLabel(transaction)}:{" "}
                        {formatAddress(transaction.address)}
                      </div>
                    )}
                  {transaction.transactionHash && (
                    <div className="transaction-footnote">
                      <span>Tx:&nbsp;</span>
                      {explorerLink ? (
                        <a
                          className="transaction-link"
                          href={explorerLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {formatHash(transaction.transactionHash)}
                        </a>
                      ) : (
                        <span className="transaction-hash">
                          {formatHash(transaction.transactionHash)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default History;
